/**
 * Helpers for resolving the user-declared "label column" for a table
 * (`DatabaseMetaInfo.tableDisplayColumns`). The persisted record is keyed by
 * `(schema, table)` and applies to every loose FK targeting the table — so
 * once a user picks `Customers.name`, every FK pointing at `Customers` gets
 * the same label automatically.
 *
 * Sentinels on the persisted side:
 *  - `column === ''` → explicit "no label column" (user said don't show one).
 *  - no entry at all → never declared; callers fall back to a heuristic.
 *
 * `resolveTableDisplayColumn` collapses both shapes into a `string | null` for
 * consumers that just want "what column should we render as the label".
 */

import type { DatabaseMetaInfo, TableDisplayColumn } from '@/types/electron';

export function findTableDisplayColumn(
  // Only the one field is read, so a caller holding just the record list (the
  // MetaInfo manager's reference-table rows) can share the identity rule too.
  metaInfo: Pick<DatabaseMetaInfo, 'tableDisplayColumns'> | null | undefined,
  schema: string,
  table: string,
): TableDisplayColumn | undefined {
  if (!metaInfo?.tableDisplayColumns) return undefined;
  return metaInfo.tableDisplayColumns.find(
    (e) => e.schema === schema && e.table === table,
  );
}

/**
 * Resolve the persisted label column for a target table to a concrete column
 * name (or `null` for "explicit none"), gracefully handling stale entries
 * whose persisted column no longer exists in the live schema.
 *
 * Returns:
 *  - `string`  — the user-declared column to use as the label.
 *  - `null`    — user has explicitly chosen "no label column", or the
 *               persisted column is no longer present in `availableColumns`.
 *               Callers should respect this and not run the heuristic.
 *  - `undefined` — no declaration exists for this table; callers may fall
 *                  back to `pickDisplayColumn` (the name-based heuristic).
 *
 * The "persisted column missing from schema" case maps to `null` rather than
 * `undefined` on purpose: the user expressed intent for this table and we
 * shouldn't silently re-fire the heuristic to overwrite that. The picker UI
 * is the single place that re-prompts the user when their column disappears.
 */
export function resolveTableDisplayColumn(
  metaInfo: DatabaseMetaInfo | null | undefined,
  schema: string,
  table: string,
  availableColumns: readonly string[],
): string | null | undefined {
  const entry = findTableDisplayColumn(metaInfo, schema, table);
  if (!entry) return undefined;
  if (entry.column === '') return null;
  const live = availableColumns.find(
    (c) => c.toLowerCase() === entry.column.toLowerCase(),
  );
  return live ?? null;
}
